/**
 * Client-SDK specialization of the `runHangReports` schema, plus typed
 * collection ref + write helper. Server-time is threaded explicitly through
 * {@link writeRunHangReport} via a `serverTimestamp()` sentinel (same pattern
 * as `chatAudit`) so beacons carry server-authoritative time.
 */
import {
  addDoc,
  collection,
  getFirestore,
  type CollectionReference,
  type FieldValue,
  type Firestore,
} from 'firebase/firestore';
import {z} from 'zod';
import {
  ClientRunHangReportSchema as ClientRunHangReportBaseSchema,
  getRunHangReportsCollectionPath,
  type ClientRunHangReport,
} from '../documents/runHangReport';
import {ClientTimestampSchema, createConverter} from './sdk';

/** Stored doc view for readers (client-SDK `Timestamp` on read). */
export const StoredRunHangReportSchema = ClientRunHangReportBaseSchema.extend({
  createdAt: ClientTimestampSchema,
}).loose();

/** Inferred type (client-SDK timestamps). */
export type StoredRunHangReport = z.infer<typeof StoredRunHangReportSchema>;

const runHangReportConverter = createConverter({
  schema: StoredRunHangReportSchema,
  options: {
    // Read-side validation guarantees readers see well-formed, Timestamp-typed
    // events. Skip write-side validation because `createdAt` rides a
    // `serverTimestamp()` sentinel (FieldValue), not a resolved Timestamp.
    validateOnRead: true,
    validateOnWrite: false,
  },
});

/**
 * Typed collection reference for `runHangReports`. Admin read tooling should
 * use this so subscriptions get validated {@link StoredRunHangReport}s.
 *
 * @param args.firestore Firestore instance. Defaults to `getFirestore()`.
 * @return A `CollectionReference<StoredRunHangReport>`.
 */
export function getRunHangReportsCollectionRef({
  firestore = getFirestore(),
}: {firestore?: Firestore} = {}): CollectionReference<StoredRunHangReport> {
  return collection(firestore, getRunHangReportsCollectionPath()).withConverter(
    runHangReportConverter,
  );
}

/**
 * Writes a prepared {@link ClientRunHangReport} via the typed collection ref.
 * The caller supplies the `serverTimestamp()` sentinel explicitly — no hidden
 * augmentation — so every call site shows its server-time attribution.
 *
 * @param args.event Client-prepared report payload.
 * @param args.serverNow `serverTimestamp()` sentinel from `firebase/firestore`.
 * @param args.firestore Firestore instance. Defaults to `getFirestore()`.
 * @return Resolves after the write completes.
 * @throws Propagates Firestore errors; callers decide whether to swallow.
 */
export async function writeRunHangReport({
  event,
  serverNow,
  firestore = getFirestore(),
}: {
  event: ClientRunHangReport;
  serverNow: FieldValue;
  firestore?: Firestore;
}): Promise<void> {
  const ref = getRunHangReportsCollectionRef({firestore});
  await addDoc(ref, {...event, createdAt: serverNow});
}
